import { Alert, Box, Card, CardContent, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { InfoOutlined, LockOutlined } from '@mui/icons-material';
import type { WidgetResult } from './dashboardApi';
import { RANGE_LABELS } from './dashboardApi';
import { WIDGET_VIEWS, WidgetBodySkeleton } from './widgetViews';

/** One widget in its card, with loading / error / permission-denied states that never affect its neighbours. */
export function WidgetCard({ widget, loading }: { widget?: WidgetResult; loading?: boolean }) {
  const data = widget?.data;
  const View = widget ? WIDGET_VIEWS[widget.id] : undefined;
  const caption = [data?.period, data?.scope].filter(Boolean).join(' · ');
  return (
    <Card sx={{ height: '100%' }} data-testid={widget ? `widget-${widget.id}` : undefined}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{widget?.title ?? ' '}</Typography>
            {caption && <Typography variant="caption" color="text.secondary">{caption}</Typography>}
            {!caption && widget?.filters.range && <Typography variant="caption" color="text.secondary">{RANGE_LABELS[widget.filters.range]}</Typography>}
          </Box>
          {widget && (
            <Tooltip title={widget.definition} arrow>
              <IconButton size="small" aria-label={`What ${widget.title} counts`}><InfoOutlined fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </Box>
        {loading || !widget ? <WidgetBodySkeleton />
          : widget.status === 'denied' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', py: 1 }}>
              <LockOutlined fontSize="small" /><Typography variant="body2">You do not have access to this widget.</Typography>
            </Box>
          )
          : widget.status === 'error' ? <Alert severity="warning">{widget.error ?? 'This widget could not be loaded.'}</Alert>
          : !View || !data ? <Alert severity="info">This widget type is not supported by this version of the app.</Alert>
          : View({ data })}
      </CardContent>
    </Card>
  );
}

export function WidgetGrid({ widgets }: { widgets: WidgetResult[] }) {
  return (
    <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
      {widgets.map((w) => (
        // Narrow spans stack to full width on phones and halve on tablets so the grid stays usable at every size.
        <Grid key={w.id} item xs={12} sm={w.span <= 6 ? 6 : 12} md={w.span}>
          <WidgetCard widget={w} />
        </Grid>
      ))}
    </Grid>
  );
}
